/* eslint-disable no-nested-ternary */
/* eslint-disable react/no-array-index-key */
/* eslint-disable @typescript-eslint/no-use-before-define */
import React, { FC, ReactElement, useEffect, useContext, useState } from 'react';
import { PrefferencesContext } from '../../context/context';
import { getMatches } from '../../services/http/httpService';
import { IFootballMatch, IFootballLiveMatchScore } from '../../interfaces/interfaces';
import { REFRESH_INTERVAL, TEASER_WIDGET_WIDTH } from '../../config/config';
import TableLoader from '../loader/loader';
import {
  trans,
  getRealGameTimestamp,
  getCompetitionIdQuery,
  getSeasonIdQuery,
  getDays,
  getMonths
} from '../../helpers/helperFunctions';

const Teaser: FC = (): ReactElement => {
  const { language, widgetWidth } = useContext(PrefferencesContext);

  const [loading, setLoading] = useState<boolean>(true);

  const [matches, setMatches] = useState<IFootballMatch[]>([]);

  // Set loading to false when matches are fetched
  useEffect(() => {
    if (matches.length) {
      setLoading(false);
    }
  }, [matches]);

  // Fetches standings when a competition is selected manually or on load
  useEffect(() => {
    if (!language) return undefined;
    setLoading(true);
    const competitionId = getCompetitionIdQuery();
    const seasonId = getSeasonIdQuery();
    getMatches(language, competitionId, seasonId)
      .then((response) => {
        const mapResponse = response.map((item) => ({
          ...item,
          date: getRealGameTimestamp(item.date)
        }));
        setMatches(mapResponse);
      })
      .catch((e) => {
        console.log('Error fetching standings');
        setLoading(false);
      });

    // Register interval to re-fetch matches
    const standingsInterval = setInterval(() => {
      getMatches(language, competitionId, seasonId)
        .then((response) => {
          const mapResponse = response.map((item) => ({
            ...item,
            date: getRealGameTimestamp(item.date)
          }));
          setMatches(mapResponse);
        })
        .catch((e) => {
          console.log('Error fetching standings');
        });
    }, REFRESH_INTERVAL);

    return () => clearInterval(standingsInterval);
  }, [language]);

  return <>{createMatches(matches, loading, widgetWidth)}</>;
};

function createMatches(
  matches: IFootballMatch[],
  loading: boolean,
  widgetWidth: number
): ReactElement {
  return createDateGrouppings(matches, loading, widgetWidth);
}

function createDateGrouppings(
  matches: IFootballMatch[],
  loading: boolean,
  widgetWidth: number
): ReactElement {
  matches.sort((a, b) => +a.date - +b.date);
  const now = new Date();
  let tomorrow = new Date();
  let afterTomorrow = new Date();

  // set tomorrow and after tomorrow from current date
  tomorrow = new Date(tomorrow.setDate(tomorrow.getDate() + 1));
  afterTomorrow = new Date(afterTomorrow.setDate(afterTomorrow.getDate() + 2));

  // filter all matches after current date
  const afterDateMatches = matches.filter((d) => {
    const lastMidnight = new Date().setHours(0, 0, 0, 0);
    const gameFinished = d.finished === 'yes';
    if (d.date > lastMidnight) return true;
    if (d.date > lastMidnight - 14400000 && !gameFinished) return true;
    return false;
  });

  // set  groupping count avaiable for specific screen
  const grouppingsCount = widgetWidth < 712 ? 1 : widgetWidth < 1024 ? 2 : 3;

  const dateGrouppings = Array.from(
    new Set(
      afterDateMatches.map((match) => {
        const newDate = new Date(match.date);
        const dayName =
          now.getDate() === newDate.getDate() ||
          (now.getDate() - 1 === newDate.getDate() && match.finished === 'no')
            ? trans('today')
            : tomorrow.getDate() === newDate.getDate()
            ? trans('tomorrow')
            : afterTomorrow.getDate() === newDate.getDate()
            ? trans('afterTomorrow')
            : getDays()[newDate.getDay()];
        const date = newDate.getDate();
        const monthName = getMonths()[newDate.getMonth()];
        return `${dayName}, ${date}. ${monthName}`;
      })
    )
  );

  const matchesByDate = dateGrouppings.map((matchDate, dateIndex) => {
    const matchesFilteredByDate = matches.filter((match) => {
      const newDate = new Date(match.date);
      const dayName =
        now.getDate() === newDate.getDate() ||
        (now.getDate() - 1 === newDate.getDate() && match.finished === 'no')
          ? trans('today')
          : tomorrow.getDate() === newDate.getDate()
          ? trans('tomorrow')
          : afterTomorrow.getDate() === newDate.getDate()
          ? trans('afterTomorrow')
          : getDays()[newDate.getDay()];
      const date = newDate.getDate();
      const monthName = getMonths()[newDate.getMonth()];
      return `${dayName}, ${date}. ${monthName}` === matchDate;
    });
    return (
      <div className="treaser-matches-by-date-list" key={matchDate + dateIndex}>
        <div className="match-date">{matchDate}</div>
        {createGrouppedMatches(matchesFilteredByDate, loading, widgetWidth)}
      </div>
    );
  });

  return (
    <div>
      <div className="treaser-matches-by-date">{matchesByDate.slice(0, grouppingsCount)}</div>
    </div>
  );
}

function createGrouppedMatches(
  matches: IFootballMatch[],
  loading: boolean,
  widgetWidth: number
): ReactElement {
  return loading ? (
    <TableLoader viewBox="10 10 1200 720" />
  ) : (
    <div className="matches-container">
      <table className="matches-table">
        <tbody>
          {matches.map((row, index) => {
            const {
              home,
              homeShort,
              homeCountryCode,
              showHomeTeam,
              away,
              awayShort,
              awayCountryCode,
              showAwayTeam,
              teamLogoUrlHome,
              teamLogoUrlAway,
              date,
              liveScore,
              postponed
            } = row;

            const homeName =
              widgetWidth <= TEASER_WIDGET_WIDTH && showHomeTeam === 'yes' && homeCountryCode
                ? homeCountryCode
                : homeShort;
            const awayName =
              widgetWidth <= TEASER_WIDGET_WIDTH && showAwayTeam === 'yes' && awayCountryCode
                ? awayCountryCode
                : awayShort;
            return (
              <tr key={`${home}${away}${index}${Math.random()}`}>
                <td>
                  {showHomeTeam === 'yes' && <img src={teamLogoUrlHome} alt="Team logo" />}
                  {homeName}
                </td>
                {createScoreRow(date, liveScore, postponed)}
                <td>
                  {awayName}
                  {showAwayTeam === 'yes' && <img src={teamLogoUrlAway} alt="Team logo" />}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function createScoreRow(
  gameDate: string | number,
  liveScore: IFootballLiveMatchScore,
  postponed: boolean
) {
  if (postponed) return <td>{trans('postponed')}</td>;
  const notStarted = gameDate > Date.now();
  if (notStarted) {
    const newDate = new Date(gameDate);
    const date = newDate.getDate();
    const month = newDate.getMonth() + 1;
    const hour = newDate.getHours();
    const minutes = newDate.getMinutes() !== 0 ? newDate.getMinutes() : '00';
    const displayTime = `${date}.${month}. - ${hour}:${minutes}`;
    return <td className="match-time">{displayTime}</td>;
  }
  return <td className="match-score">{createScoreString(liveScore)}</td>;
}

function createScoreString(liveScore: IFootballLiveMatchScore) {
  if (!liveScore.currentScore) {
    return (
      <div>
        {!liveScore?.penaltyScore ? (
          <p>
            {liveScore?.extraTime?.home}:{liveScore?.extraTime?.away} ({liveScore?.halfTime?.home}:
            {liveScore?.halfTime?.away}, {liveScore?.fullTime?.home}:{liveScore?.fullTime?.away})
          </p>
        ) : (
          <>
            <p>
              {liveScore?.extraTime?.home}:{liveScore?.extraTime?.away}
            </p>
            <p className="penalty-score">
              ({liveScore?.penaltyScore?.home}:{liveScore?.penaltyScore?.away}{' '}
              {trans('penalties-short')})
            </p>
          </>
        )}
      </div>
    );
  }
  return `${liveScore.currentScore.home}:${liveScore.currentScore.away} ${
    liveScore.halfTime
      ? `(${liveScore.halfTime.home}:${liveScore.halfTime.away}${
          liveScore.fullTime ? `, ${liveScore.fullTime.home}:${liveScore.fullTime.away}` : ''
        })`
      : ''
  }`;
}

export default Teaser;
